import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { Divider, Grid } from "@mui/material";
import FirstNameInput from "../../../../../components/inputs/firstName/firstNameInput";
import LastNameInput from "../../../../../components/inputs/lastName/lastNameInput";
import EmailInput, {
  checkEmail,
} from "../../../../../components/inputs/email/emailInput";
import React from "react";
import NewCoachProps from "./interface/newCoachProps";
import SearchUserProps from "./interface/searchUserProps";
import RenderSearchInput from "./src/renderSearchInput";
import RenderTeamsLink from "./src/renderTeamsLink";
import axios from "axios";
import { endpoints } from "../../../../../endpoints/endpoints";
import { User, AppContext } from "../../../../../context/appContext";
import { enqueueSnackbar } from "notistack";
import GenderInput, {
  GenderProps,
} from "../../../../../components/inputs/gender/genderInput";
import { CoachListContext } from "../../context/coachListContext";

const AddCoachDialog = (): React.JSX.Element => {
  const {
    addCoach,
    setAddCoach,
    fetchCoachList,
  }: {
    addCoach: boolean;
    setAddCoach: React.Dispatch<React.SetStateAction<boolean>>;
    fetchCoachList: () => Promise<void>;
  } = React.useContext(CoachListContext);

  const [newCoach, setNewCoach] = React.useState<NewCoachProps>({
    firstName: "",
    lastName: "",
    email: "",
    gender: null,
    teamsLink: "",
  });

  const [searchUser, setSearchUser] = React.useState<SearchUserProps>({
    loading: false,
    searchString: "",
    searchResults: [],
    selectedParticipant: null,
  });

  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const handleClose = (): void => setAddCoach(false);

  const identifyGenderForBackend = (): "m" | "f" | "d" => {
    // backend only knows "m" and "f" but not "Herr" and "Frau"
    switch (newCoach.gender?.value) {
      case "f4285c6f-8b98-4ee8-bd61-7beb59f1df7b":
        return "m";

      case "96972cc0-a7e7-44b7-a618-7f43e75a8811":
        return "f";

      default:
        return "d";
    }
  };

  const makeUserToCoach = (): void => {
    const payload = {
      sessionId: user.sessionId,
      firstName: newCoach.firstName,
      lastName: newCoach.lastName,
      email: newCoach.email,
      teamsLink: newCoach.teamsLink,
      gender: identifyGenderForBackend(),
      participantId: searchUser.selectedParticipant?.participantId,
    };

    axios
      .post(endpoints.admin.makeNewCoach, payload)
      .then((response) => {
        checkSession(response);

        if (response.data.meta.status === "success") {
          fetchCoachList();
          setAddCoach(false);
          enqueueSnackbar("Coach erfolgreich hinzugefügt!", {
            variant: "success",
          });
        } else {
          enqueueSnackbar("Fehler beim Hinzufügen des Coaches!", {
            variant: "error",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        enqueueSnackbar("Fehler beim Hinzufügen des Coaches!", {
          variant: "error",
        });
      });
  };

  return (
    <Dialog
      maxWidth="lg"
      open={addCoach}
      onClose={handleClose}
      aria-labelledby={""}
    >
      <DialogTitle id={"create-new-coach"}>Neuen Coach anlegen</DialogTitle>

      <Divider />

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RenderSearchInput
              searchUser={searchUser}
              setSearchUser={setSearchUser}
              newCoach={newCoach}
              setNewCoach={setNewCoach}
            />
          </Grid>

          <React.Fragment>
            <Grid item xs={12} md={6}>
              <FirstNameInput
                disabled
                required
                firstName={newCoach.firstName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LastNameInput disabled required lastName={newCoach.lastName} />
            </Grid>

            <Grid item xs={8}>
              <EmailInput
                disabled={!searchUser.selectedParticipant}
                required
                email={newCoach.email}
                setEmail={(email: string) =>
                  setNewCoach({
                    ...newCoach,
                    email: email,
                  })
                }
              />
            </Grid>

            <Grid item xs={4}>
              <GenderInput
                disabled={!searchUser.selectedParticipant}
                required
                gender={newCoach.gender}
                setGender={(
                  option: {
                    label: string;
                    value: string;
                  } | null
                ) => {
                  setNewCoach({
                    ...newCoach,
                    gender: option as GenderProps,
                  });
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <RenderTeamsLink
                newCoach={newCoach}
                setNewCoach={setNewCoach}
                searchUser={searchUser}
              />
            </Grid>
          </React.Fragment>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: 2,
        }}
      >
        <Button onClick={handleClose} color="primary">
          Abbrechen
        </Button>

        <Button
          disabled={
            !newCoach.firstName ||
            !newCoach.lastName ||
            !checkEmail(newCoach.email) ||
            !newCoach.gender ||
            !newCoach.teamsLink
          }
          onClick={makeUserToCoach}
          variant="contained"
          color="primary"
        >
          User zum Coach machen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCoachDialog;
